import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import {
    TextField,
    Box,
    Backdrop,
    Stack,
    InputLabel,
    MenuItem,
    FormControl,
    Select
} from '@mui/material';

import CustomButton from '../custom_components/CustomButton';
import CustomModalHeader from '../custom_components/CustomModalHeader';

import { getPhilippinesDate } from '../../utils/getPhilippinesDate';
import { getArrivedListId } from '../../utils/getArrivedListId';

const apiUrl = process.env.REACT_APP_API_URL;

const CreateArrivedList = ({ open, onClose, onUpdate }) => {

    const [listId, setListId] = useState("");
    const [listName, setListName] = useState("");
    const [cargo, setCargo] = useState("Sea Cargo");
    const [date, setDate] = useState(getPhilippinesDate());
    const [isSaving, setIsSaving] = useState(false);

    // INITIALIZED DATA
    useEffect(() => {
        if (!open) return;

        let cancelled = false;

        const initializeForm = async () => {
            setListId("");
            setListName("");
            setCargo("Sea Cargo");
            setDate(getPhilippinesDate());

            const id = await getArrivedListId();

            if (!cancelled) {
                setListId(id);
            }
        };

        initializeForm();

        return () => {
            cancelled = true;
        };
    }, [open]);

    // HANDLE SAVE LIST
    const handleSaveList = async () => {
        if (!listId || !listName || !date || !cargo) {
            Swal.fire({ text: "Please fill all the required inputs.", icon: "warning" });
            return;
        }

        setIsSaving(true);

        try {
            const response = await fetch(
                `${apiUrl}manage_arrived/create_arrived_list.php`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        listId: listId,
                        listName: listName,
                        cargo: cargo,
                        dateArrived: date,
                    })
                });

            const result = await response.json();

            if (result.status === 'success') {

                onUpdate({
                    ListID: listId,
                    ListName: listName,
                    Cargo: cargo,
                    DateArrived: date,
                    TotalItems: 0,
                    Remarks: "",
                });

                Swal.fire({
                    icon: "success",
                    text: result.message,
                    timer: 3500,
                    showConfirmButton: false,
                });

                onClose();

            } else if (result.status === 'error') {

                Swal.fire({
                    text: result.message,
                    icon: "error"
                });

            }
        } catch (error) {
            Swal.fire({
                title: 'Error saving list!',
                text: error, icon: "error"
            });
            console.error('Error saving list:', error);
        } finally {
            setListId("");
            setListName("");
            setCargo("");
            setDate(date);
            setIsSaving(false);
        }
    };

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: 'blur(1px)'
            }}
        >
            <Box
                sx={{
                    width: 420,
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    p: 2.5,
                    boxShadow: 6,

                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >

                {/* HEADER */}
                <CustomModalHeader
                    title="Create New List"
                    subtitle={`ID: ${listId || "Generating..."}`}
                    onClose={onClose}
                />

                {/* INPUT FORM */}
                <Stack spacing={2.2}>
                    <TextField
                        label="List Name"
                        size="small"
                        fullWidth
                        value={listName}
                        onChange={(e) => setListName(e.target.value)}
                    />

                    <FormControl
                        sx={{ backgroundColor: 'white', borderRadius: 1 }}
                        size="small"
                    >
                        <InputLabel id="select-cargo">
                            Cargo Type
                        </InputLabel>
                        <Select
                            labelId="select-cargo"
                            id="demo-select-small"
                            value={cargo}
                            label="Cargo Type"
                            onChange={(e) => setCargo(e.target.value)}
                        >
                            <MenuItem value="Sea Cargo">Sea Cargo</MenuItem>
                            <MenuItem value="Air Cargo">Air Cargo</MenuItem>
                            <MenuItem value="PH">PH</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Date"
                        type="date"
                        size="small"
                        fullWidth
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                    />
                </Stack>

                {/* ACTION BUTTONS */}
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={2}
                    mt={4}
                >

                    <CustomButton
                        label="Cancel"
                        variant="outlined"
                        hoverColor="#d14249"
                        onClick={onClose}
                    />

                    <CustomButton
                        label={isSaving ? "Saving..." : "Save"}
                        disabled={isSaving || !listId}
                        onClick={handleSaveList}
                    />

                </Stack>

            </Box>

        </Backdrop >
    );
};

export default CreateArrivedList;
